import React, { useEffect, useMemo } from 'react';
import { Spinner } from '@vw-marketing/us-components';
import { ContentContainer, ContentLayout, FullWidthContainer } from '../styles';
import { Box, TextBox } from '../../Box';
import { Section } from '../../Section';
import { useStore } from '../../../store/store';
import { getAllIndexableSections } from '../../../utils/navigation';
import { useSectionLoader } from '../../../hooks/custom/use-section-loader';
import { useApolloClient } from '@apollo/client';
import { CoverImageFeature } from '../../Section/features';
import { usePrintPageStyles } from '../../../hooks/custom/use-print-page-styles';
import { normalizeSectionId } from '../../../utils/query-param-helpers';

interface FullQSGTemplateProps {
  isPrintMode: boolean;
  hasInit: boolean;
  /**
   * If true, renders the template hidden (for PDF generation)
   * When hidden, isPrintMode logic still applies but styles are adjusted
   */
  isHidden?: boolean;
}

export const FullQSGTemplate: React.FC<FullQSGTemplateProps> = ({
  isPrintMode,
  hasInit,
  isHidden = false,
}) => {
  const [state] = useStore();
  const { qsgNavigation, qsgSectionMap, qsgSectionLoading, qsgSectionErrors } =
    state.qsg;

  const client = useApolloClient();
  const { loadSection } = useSectionLoader(client, false);

  // Inject @page styles for PDF printing (when in print mode or hidden mode)
  usePrintPageStyles(isPrintMode || isHidden);

  const navSections = qsgNavigation?.sections ?? [];
  const applyPrintMode = isPrintMode || isHidden;

  // Get all indexable sections in order
  // When hidden, we still want to load sections even if isPrintMode is false
  const shouldLoadSections = applyPrintMode;
  const allIndexableSections = useMemo(() => {
    if (!shouldLoadSections || !qsgNavigation) return [];
    return getAllIndexableSections(navSections);
  }, [shouldLoadSections, qsgNavigation, navSections]);

  // Load all sections when in print mode or hidden mode
  useEffect(() => {
    if (!shouldLoadSections || !qsgNavigation || !hasInit) return;

    allIndexableSections.forEach((section) => {
      // Normalize section ID for map lookups
      const normalizedSectionId = normalizeSectionId(section.sectionId);
      const sectionData = qsgSectionMap[normalizedSectionId];
      const isLoading = qsgSectionLoading[normalizedSectionId];
      const hasError = qsgSectionErrors[normalizedSectionId];

      // Load section if not already loaded, loading, or has error
      if (!sectionData && !isLoading && !hasError && section._path) {
        loadSection(section._path, section.sectionId);
      }
    });
  }, [
    hasInit,
    shouldLoadSections,
    allIndexableSections,
    qsgSectionMap,
    qsgSectionLoading,
    qsgSectionErrors,
    loadSection,
    qsgNavigation,
  ]);

  const sectionsToRender = useMemo(() => {
    if (!shouldLoadSections) return [];
    return allIndexableSections
      .map((section) => {
        const normalizedSectionId = normalizeSectionId(section.sectionId);
        return qsgSectionMap[normalizedSectionId];
      })
      .filter(Boolean);
  }, [shouldLoadSections, allIndexableSections, qsgSectionMap]);

  const isLoading = useMemo(() => {
    if (!shouldLoadSections) return false;
    return allIndexableSections.some(
      (section) => qsgSectionLoading[section.sectionId]
    );
  }, [shouldLoadSections, allIndexableSections, qsgSectionLoading]);

  const hasError = useMemo(() => {
    if (!shouldLoadSections) return false;
    const errorSection = allIndexableSections.find(
      (section) => qsgSectionErrors[section.sectionId]
    );
    return errorSection ? qsgSectionErrors[errorSection.sectionId] : null;
  }, [shouldLoadSections, allIndexableSections, qsgSectionErrors]);

  // Don't render if not in print mode and not hidden
  if (!isPrintMode && !isHidden) return null;

  if (isLoading) {
    return (
      <ContentLayout $isPrintMode={isPrintMode}>
        <ContentContainer $isPrintMode={isPrintMode}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Spinner />
          </Box>
        </ContentContainer>
      </ContentLayout>
    );
  }

  if (hasError) {
    return (
      <ContentLayout $isPrintMode={isPrintMode}>
        <ContentContainer $isPrintMode={isPrintMode}>
          <Box>
            <TextBox>Error loading sections: {hasError}</TextBox>
          </Box>
        </ContentContainer>
      </ContentLayout>
    );
  }

  const renderCoverImage = Boolean(qsgNavigation?.coverImage?._publishUrl);

  return (
    <ContentLayout
      $isPrintMode={applyPrintMode}
      style={
        isHidden
          ? {
              position: 'fixed',
              left: '-9999px',
              top: '0',
              visibility: 'hidden',
              width: '816px', // Force PDF page width (US Letter at 96 DPI)
              height: 'auto',
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: -1,
            }
          : undefined
      }
    >
      {renderCoverImage && (
        <FullWidthContainer
          $isPrintMode={applyPrintMode}
          className='print-container'
        >
          <CoverImageFeature
            imgUrl={qsgNavigation?.coverImage?._publishUrl!}
            isPrintMode={applyPrintMode}
          />
        </FullWidthContainer>
      )}
      {sectionsToRender.map((section) => (
        <ContentContainer
          key={`section-${section.sectionId}`}
          $isPrintMode={applyPrintMode}
          className='print-container'
        >
          <Section
            key={`section-${section.sectionId}`}
            section={section}
            isPrintMode={applyPrintMode}
          />
        </ContentContainer>
      ))}
    </ContentLayout>
  );
};
